import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Save, Trash2, X } from 'lucide-react';
import api from '../../services/api';
import { field, ghost, primary, muted, card, rowBorder, errorText } from '../time/styles';

// Lifecycle setup → Forms: built-in sections (switched on, with required
// fields; answers go onto the employee's profile once HR accepts them) and
// custom questions.

const TYPE_NAMES = { TEXT: 'Short answer', LONGTEXT: 'Paragraph', NUMBER: 'Number', DATE: 'Date', CHOICE: 'Choose one', YESNO: 'Yes / no', RATING: 'Rating 1-5' };

const Editor = ({ initial, meta, say, onDone }) => {
    const [f, setF] = useState(initial);
    const [saving, setSaving] = useState(false);
    const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));
    const sections = f.sections || {};
    const toggleSection = (key) => {
        const next = { ...sections };
        if (next[key]) delete next[key]; else next[key] = { required: [] };
        set('sections', next);
    };
    const toggleRequired = (sKey, fKey) => {
        const req = sections[sKey].required || [];
        set('sections', { ...sections, [sKey]: { required: req.includes(fKey) ? req.filter(x => x !== fKey) : [...req, fKey] } });
    };
    const setField = (i, v) => set('fields', f.fields.map((x, j) => (j === i ? v : x)));

    const save = async () => {
        setSaving(true);
        try {
            const body = { purpose: f.purpose, name: f.name, sections: f.sections, fields: f.fields.map(x => ({ ...x, options: x.type === 'CHOICE' ? (typeof x.options === 'string' ? x.options.split('\n') : x.options) : [] })), isActive: f.isActive };
            const r = f.id ? await api.put(`/lifecycle/forms/${f.id}`, body) : await api.post('/lifecycle/forms', body);
            setF(r.data);
            say(true, `Saved "${r.data.name}"`);
        } catch (e) {
            say(false, errorText(e, 'Could not save the form'));
        } finally {
            setSaving(false);
        }
    };
    const remove = async () => {
        if (!window.confirm(`Delete the form "${f.name}"?`)) return;
        try {
            await api.delete(`/lifecycle/forms/${f.id}`);
            say(true, `Deleted "${f.name}"`);
            onDone();
        } catch (e) {
            say(false, errorText(e, 'Could not delete the form'));
        }
    };

    return (
        <div style={{ display: 'grid', gap: 14 }}>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <label style={{ display: 'grid', gap: 4, flex: '1 1 220px' }}><span style={muted}>Form name</span>
                    <input style={field} maxLength={100} value={f.name} onChange={e => set('name', e.target.value)} />
                </label>
                <label style={{ display: 'grid', gap: 4 }}><span style={muted}>Used for</span>
                    <select style={field} value={f.purpose} onChange={e => set('purpose', e.target.value)}>
                        {meta.purposes.map(p => <option key={p} value={p}>{meta.purposeLabels[p]}</option>)}
                    </select>
                </label>
            </div>
            <div>
                <strong style={{ color: 'var(--text-main)' }}>Standard sections</strong>
                <p style={{ ...muted, margin: '2px 0 8px' }}>Answers fill in the employee's profile (bank, PAN, contacts…) when HR accepts them. Only the last 4 digits of Aadhaar are ever asked for.</p>
                {meta.sections.map(s => (
                    <div key={s.key} style={{ borderTop: rowBorder, padding: '8px 0' }}>
                        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontWeight: 500 }}>
                            <input type="checkbox" checked={!!sections[s.key]} onChange={() => toggleSection(s.key)} />{s.label}
                        </label>
                        {sections[s.key] && (
                            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', padding: '6px 0 0 26px' }}>
                                {s.fields.map(x => (
                                    <label key={x.key} style={{ display: 'flex', gap: 4, alignItems: 'center', fontSize: 13 }}>
                                        <input type="checkbox" aria-label={`${x.label} required`} checked={(sections[s.key].required || []).includes(x.key)} onChange={() => toggleRequired(s.key, x.key)} />
                                        {x.label} <span style={muted}>required</span>
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>
                ))}
            </div>
            <div>
                <strong style={{ color: 'var(--text-main)' }}>Your own questions</strong>
                <ol style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                    {f.fields.map((q, i) => (
                        <li key={q.key || i} style={{ borderTop: rowBorder, padding: '8px 0', display: 'grid', gap: 6 }}>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                                <input aria-label={`Question ${i + 1}`} placeholder="Question" maxLength={200} style={{ ...field, flex: '1 1 240px' }} value={q.label} onChange={e => setField(i, { ...q, label: e.target.value })} />
                                <select aria-label={`Answer type, question ${i + 1}`} style={field} value={q.type} onChange={e => setField(i, { ...q, type: e.target.value })}>
                                    {meta.customTypes.map(t => <option key={t} value={t}>{TYPE_NAMES[t] || t}</option>)}
                                </select>
                                <label style={{ display: 'flex', gap: 4, alignItems: 'center' }}><input type="checkbox" checked={!!q.required} onChange={e => setField(i, { ...q, required: e.target.checked })} />Required</label>
                                <button type="button" style={ghost} aria-label={`Remove question ${i + 1}`} onClick={() => set('fields', f.fields.filter((_, j) => j !== i))}><X size={14} /></button>
                            </div>
                            {q.type === 'CHOICE' && (
                                <textarea aria-label={`Choices, question ${i + 1}`} placeholder="One choice per line" style={{ ...field, minHeight: 60 }}
                                    value={Array.isArray(q.options) ? q.options.join('\n') : q.options || ''} onChange={e => setField(i, { ...q, options: e.target.value })} />
                            )}
                        </li>
                    ))}
                </ol>
                <button type="button" style={{ ...ghost, marginTop: 8 }} onClick={() => set('fields', [...f.fields, { label: '', type: 'TEXT', required: false, options: [] }])}><Plus size={14} /> Add question</button>
            </div>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="checkbox" checked={f.isActive !== false} onChange={e => set('isActive', e.target.checked)} /><span>Available for checklists</span>
            </label>
            <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-primary" style={primary} onClick={save} disabled={saving}><Save size={14} /> {saving ? 'Saving…' : 'Save form'}</button>
                {f.id && <button style={ghost} onClick={remove}><Trash2 size={14} /> Delete</button>}
            </div>
        </div>
    );
};

const FormsTab = ({ say }) => {
    const [meta, setMeta] = useState(null);
    const [editing, setEditing] = useState(null);
    const load = useCallback(() => api.get('/lifecycle/forms').then(r => setMeta(r.data)).catch(e => say(false, errorText(e, 'Could not load forms'))), [say]);
    useEffect(() => { load(); }, [load]);

    if (!meta) return <p style={muted}>Loading…</p>;
    if (editing) {
        return (
            <section className="card" style={card}>
                <button style={{ ...ghost, marginBottom: 16 }} onClick={() => { setEditing(null); load(); }}>← All forms</button>
                <Editor key={editing.id || 'new'} initial={editing} meta={meta} say={say} onDone={() => { setEditing(null); load(); }} />
            </section>
        );
    }
    return (
        <section className="card" style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 12 }}>
                <p style={{ ...muted, margin: 0, fontSize: 14 }}>Forms people fill in as a checklist task: joining details, exit interview, and anything else.</p>
                <button className="btn btn-primary" style={primary} onClick={() => setEditing({ purpose: 'CUSTOM', name: '', sections: {}, fields: [], isActive: true })}><Plus size={14} /> New form</button>
            </div>
            {meta.forms.length === 0 ? <p style={muted}>No forms yet. "Add starter checklists" in the Checklists tab also adds a joining-details form and an exit interview.</p> : (
                <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                    {meta.forms.map(f => (
                        <li key={f.id} style={{ borderTop: rowBorder, padding: '10px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', opacity: f.isActive ? 1 : 0.6 }}>
                            <span style={{ color: 'var(--text-main)' }}>{f.name} <span style={muted}>· {meta.purposeLabels[f.purpose]} · {Object.keys(f.sections || {}).length} sections, {(f.fields || []).length} questions</span></span>
                            <button style={ghost} aria-label={`Edit ${f.name}`} onClick={() => setEditing(f)}>Edit</button>
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );
};

export default FormsTab;
